// src/app/data/navigation-categories.ts
// Navigation categories split into chunks for better tree shaking and lazy loading

import type { NavigationCategory } from './navigation-types.ts';
import { CATEGORY_PRIORITY } from './navigation-constants.ts';

// Core categories (always loaded)
export const CORE_CATEGORIES: NavigationCategory[] = [
  {
    name: "AI Tools",
    description: "Artificial Intelligence powered tools and assistants",
    icon: "brain",
    priority: CATEGORY_PRIORITY["AI Tools"],
    isVisible: true,
    items: [
      { name: "AI Chat Assistant", url: "/ai-chat", description: "Chat with AI models", keywords: ["chat", "ai", "assistant"], isPopular: true },
      { name: "Writing Assistant", url: "/writing-assistant", description: "AI-powered writing help", keywords: ["writing", "ai", "content"] },
      { name: "Text Summarizer", url: "/summarize", description: "Summarize long texts", keywords: ["summarize", "text", "ai"] },
      { name: "Code Explainer", url: "/code-explainer", description: "Explain code with AI", keywords: ["code", "explain", "ai"] },
      { name: "Text Analyzer", url: "/text-analyzer", description: "Analyze text content", keywords: ["analyze", "text", "ai"] },
      { name: "Speech-to-Text", url: "/speech-to-text", description: "Convert speech to text", keywords: ["speech", "audio", "transcription"] },
      { name: "Object Detection", url: "/object-detection", description: "Detect objects in images", keywords: ["object", "detection", "ai"] },
      { name: "ChatGPT Playground", url: "/chatgpt", description: "OpenAI ChatGPT interface", keywords: ["chatgpt", "openai", "chat"] },
      { name: "Background Removal", url: "/background-removal", description: "Remove image backgrounds", keywords: ["background", "remove", "image"] },
      { name: "AI Improvement (Text, PDF)", url: "/ai-improvement-text-pdf", description: "Analyze text, images, and improve documents", keywords: ["text", "pdf", "image", "analysis", "improvement"], isNew: true },
      { name: "AI Spreadsheet", url: "/ai-spreadsheet", description: "AI-powered spreadsheet tools", keywords: ["spreadsheet", "ai", "data"] },
      { name: "AI Content Detection", url: "/ai-content-detection", description: "Detect AI-generated content", keywords: ["ai", "detection", "content"] },
      { name: "Image Similarity", url: "/image-similarity", description: "Compare image similarity", keywords: ["image", "similarity", "compare"] },
    ]
  },
  {
    name: "Media Tools",
    description: "Video, audio, and image processing tools",
    icon: "media",
    priority: CATEGORY_PRIORITY["Media Tools"],
    isVisible: true,
    items: [
      { name: "Downloader", url: "/download", description: "Download videos and media", keywords: ["download", "video", "media"], isPopular: true },
      { name: "Converter (Video, Audio, Image)", url: "/convert", description: "Convert between formats", keywords: ["convert", "video", "audio", "image"], isPopular: true },
      { name: "Text to Speech", url: "/text-to-speech", description: "Convert text to natural speech", keywords: ["text", "speech", "audio", "voice"], isNew: true },
      { name: "Compress", url: "/compress", description: "Compress files and media", keywords: ["compress", "reduce", "size"] },
      { name: "SVG Optimizer", url: "/svg-optimizer", description: "Optimize SVG files", keywords: ["svg", "optimize", "vector"] },
      { name: "Metadata Editor", url: "/metadata-editor", description: "Edit file metadata", keywords: ["metadata", "edit", "exif"] },
      { name: "Resize Image", url: "/image-resize", description: "Resize images", keywords: ["resize", "image", "dimensions"] },
      { name: "Crop Image", url: "/image-crop", description: "Crop images", keywords: ["crop", "image", "cut"] },
      { name: "Image-to-Text OCR", url: "/ocr", description: "Extract text from images", keywords: ["ocr", "image", "text"] },
    ]
  },
];

// Secondary categories (lazy loaded)
export const SECONDARY_CATEGORIES: NavigationCategory[] = [
  {
    name: "Learning/Guides",
    description: "Educational content and coding guides",
    icon: "book",
    priority: CATEGORY_PRIORITY["Learning/Guides"],
    isVisible: true,
    items: [
      { name: "Vibe Coding", url: "/vibe-coding", description: "Learn the art of vibe coding", keywords: ["vibe", "coding", "learning", "guide"], isPopular: true },
      { name: "AI Chatbots", url: "/ai-chatbots", description: "Guide to AI chatbot development", keywords: ["ai", "chatbots", "learning", "guide"], isPopular: true },
    ]
  },
  {
    name: "Productivity",
    description: "Tools to boost your productivity",
    icon: "productivity",
    priority: CATEGORY_PRIORITY["Productivity"],
    isVisible: true,
    items: [
      { name: "Notes", url: "https://notes.eyn.tools", description: "Organize your thoughts and ideas", keywords: ["notes", "notebook", "organize"], isPopular: true, isExternal: true },
      { name: "Pomodoro Timer", url: "/pomodoro", description: "Focus timer technique", keywords: ["pomodoro", "timer", "focus"] },
      { name: "Habit Tracker", url: "/habit-tracker", description: "Track daily habits", keywords: ["habit", "tracker", "daily"] },
    ]
  },
  {
    name: "Account & Auth",
    description: "Authentication and account management",
    icon: "user",
    priority: CATEGORY_PRIORITY["Account & Auth"],
    isVisible: true,
    items: [
      { name: "Login", url: "/auth/login", description: "Sign in to your account", keywords: ["login", "signin", "auth"] },
      { name: "Sign Up", url: "/auth/signup", description: "Create a new account", keywords: ["signup", "register", "auth"] },
      { name: "Login (External)", url: "https://auth.eyn.tools/auth/login", description: "Sign in to your account", keywords: ["login", "signin", "auth"], isExternal: true },
      { name: "Sign Up (External)", url: "https://auth.eyn.tools/auth/signup", description: "Create a new account", keywords: ["signup", "register", "auth"], isExternal: true },
      { name: "Pricing", url: "/pricing", description: "View pricing plans", keywords: ["pricing", "plans", "subscription"] },
      { name: "Dashboard", url: "/dashboard", description: "Your dashboard, profile, and account settings", keywords: ["dashboard", "profile", "account", "settings", "overview"] },
    ]
  },
];

// Specialized categories (loaded on demand)
export const SPECIALIZED_CATEGORIES: NavigationCategory[] = [
  {
    name: "Troubleshooting",
    description: "Diagnostics and help for common issues",
    icon: "life-buoy",
    priority: CATEGORY_PRIORITY["Troubleshooting"],
    isVisible: true,
    items: [
      {
        name: "Mic Test",
        url: "/mic-test",
        description: "Test your microphone input level and permissions in the browser",
        keywords: ["microphone", "mic", "audio", "test", "permissions", "input", "troubleshooting"],
        isNew: true,
      },
      {
        name: "Network & Connectivity",
        url: "/network-tools",
        description: "DNS, ports, HTTP, SSL, traceroute, WHOIS, and IP geolocation diagnostics",
        keywords: ["network", "dns", "ports", "ssl", "whois", "troubleshooting", "connectivity"],
        isNew: true,
      },
      {
        name: "Speed Test",
        url: "/speed-test",
        description: "Measure ping, download, and upload speed to this site",
        keywords: ["speed test", "bandwidth", "download", "upload", "ping", "latency", "network"],
        isNew: true,
      },
    ],
  },
  {
    name: "Text Tools",
    description: "Text processing and manipulation tools",
    icon: "text",
    priority: CATEGORY_PRIORITY["Text Tools"],
    isVisible: true,
    items: [
      { name: "Text Tools", url: "/tools/text", description: "All text processing tools in one place", keywords: ["text", "tools", "processing", "consolidated"], isPopular: true },
      { name: "JSON Formatter", url: "/tools/text?tab=json", description: "Format and validate JSON", keywords: ["json", "format", "validate"] },
      { name: "Base64 Encoder/Decoder", url: "/tools/text?tab=base64", description: "Encode/decode Base64", keywords: ["base64", "encode", "decode"] },
      { name: "Markdown Previewer", url: "/tools/text?tab=markdown", description: "Preview markdown", keywords: ["markdown", "preview", "render"] },
      { name: "URL Encoder/Decoder", url: "/tools/text?tab=url-encode", description: "Encode/decode URLs", keywords: ["url", "encode", "decode"] },
      { name: "Text Case Converter", url: "/tools/text?tab=text-case", description: "Convert text case", keywords: ["text", "case", "convert"] },
      { name: "Word Counter", url: "/tools/text?tab=word-counter", description: "Count words and characters", keywords: ["word", "counter", "text"] },
      { name: "Regex Tester", url: "/tools/text?tab=regex", description: "Test regular expressions", keywords: ["regex", "test", "pattern"] },
      { name: "Diff Checker", url: "/tools/text?tab=diff", description: "Compare text differences", keywords: ["diff", "compare", "text"] },
      { name: "Text Encryption", url: "/tools/text?tab=encrypt", description: "Encrypt text with custom alphabet", keywords: ["encrypt", "cipher", "security"], isNew: true },
      { name: "Sentiment Analyzer", url: "/sentiment-analyzer", description: "Analyze text sentiment", keywords: ["sentiment", "analyze", "text"] },
      { name: "Keyword Extractor", url: "/keyword-extractor", description: "Extract keywords from text", keywords: ["keyword", "extract", "text"] },
      { name: "Readability Checker", url: "/readability", description: "Check text readability", keywords: ["readability", "check", "text"] },
    ]
  },
  {
    name: "Developer Utilities",
    description: "Tools for developers and programmers",
    icon: "code",
    priority: CATEGORY_PRIORITY["Developer Utilities"],
    isVisible: true,
    items: [
      { name: "HTTP Request Builder", url: "/http-request", description: "Build HTTP requests", keywords: ["http", "request", "api"] },
      { name: "Webhook Tester", url: "/webhook-tester", description: "Test webhooks", keywords: ["webhook", "test", "api"] },
      { name: "CSS Formatter & Minifier", url: "/css-formatter", description: "Format and minify CSS", keywords: ["css", "format", "minify"] },
      { name: "YAML ↔ JSON Converter", url: "/yaml-converter", description: "Convert YAML to JSON", keywords: ["yaml", "json", "convert"] },
      { name: "CSV to JSON Converter", url: "/csv-to-json", description: "Convert CSV to JSON", keywords: ["csv", "json", "convert"] },
      { name: "EYN PC Scripts", url: "/eyn-pc-scripts", description: "PC automation scripts", keywords: ["scripts", "pc", "automation"] },
      {
        name: "FiveM Server Lookup",
        url: "/fivem",
        description: "Look up FiveM servers from Cfx.re join links or join codes",
        keywords: ["fivem", "cfx", "gta", "server", "join", "roleplay"],
        isNew: true,
      },
    ]
  },
  {
    name: "Finance",
    description: "Financial calculation tools",
    icon: "finance",
    priority: CATEGORY_PRIORITY["Finance"],
    isVisible: true,
    items: [
      { name: "Currency Converter", url: "/currency", description: "Convert currencies", keywords: ["currency", "convert", "money"] },
      { name: "Tax Calculator", url: "/tax-calculator", description: "Calculate taxes", keywords: ["tax", "calculator", "finance"] },
      { name: "Budget Allocator (50/30/20)", url: "/budget-allocator", description: "Split income across needs, wants, and savings", keywords: ["budget", "50/30/20", "allocation"] },
      { name: "Freelance Rate Calculator", url: "/freelance-rate", description: "Hourly/day rate from income goal and costs", keywords: ["freelance", "rate", "hourly", "pricing"] },
    ]
  },
  {
    name: "Generators",
    description: "Generate various types of content and data",
    icon: "generator",
    priority: CATEGORY_PRIORITY["Generators"],
    isVisible: true,
    items: [
      { name: "Generators", url: "/tools/generators", description: "All generators in one place", keywords: ["generators", "tools", "consolidated"], isPopular: true },
      { name: "QR Code Generator", url: "/tools/generators?tab=qr", description: "Generate QR codes", keywords: ["qr", "code", "generator"] },
      { name: "Password Generator", url: "/tools/generators?tab=password", description: "Generate secure passwords", keywords: ["password", "generator", "security"] },
      { name: "Hash Generator", url: "/tools/generators?tab=hash", description: "Generate hashes", keywords: ["hash", "generator", "crypto"] },
      { name: "Name Generator", url: "/tools/generators?tab=name", description: "Generate random names", keywords: ["name", "generator", "random"] },
      { name: "Random Number Generator", url: "/tools/generators?tab=random", description: "Generate random numbers", keywords: ["random", "number", "generator"] },
      { name: "Lorem Ipsum Generator", url: "/tools/generators?tab=lorem", description: "Generate Lorem Ipsum text", keywords: ["lorem", "ipsum", "text"] },
      { name: "Color Palette Generator", url: "/tools/generators?tab=colors", description: "Generate color palettes", keywords: ["color", "palette", "generator"] },
      { name: "Markdown Table Generator", url: "/tools/generators?tab=table", description: "Generate markdown tables", keywords: ["markdown", "table", "generator"] },
      { name: "UUID Generator", url: "/tools/generators?tab=uuid", description: "Generate UUIDs", keywords: ["uuid", "generator", "unique"] },
      { name: "CUID2 Generator", url: "/cuid2", description: "Generate CUID2s", keywords: ["cuid2", "generator", "unique"] },
      { name: "NanoID Generator", url: "/nanoid", description: "Generate NanoIDs", keywords: ["nanoid", "generator", "unique"] },
      { name: "Fake Identity Generator", url: "/fake-identity-generator", description: "Generate fake identities", keywords: ["fake", "identity", "generator"] },
      { name: "Slug Generator", url: "/slug-generator", description: "Generate URL slugs", keywords: ["slug", "url", "generator"] },
    ]
  },
  {
    name: "Security",
    description: "Security and privacy tools",
    icon: "shield",
    priority: CATEGORY_PRIORITY["Security"],
    isVisible: true,
    items: [
      { name: "JWT Decoder", url: "/jwt-decoder", description: "Decode JWT tokens", keywords: ["jwt", "decode", "token"] },
      { name: "Password Strength Meter", url: "/password-strength", description: "Check password strength", keywords: ["password", "strength", "security"] },
      { name: "Anonymization", url: "/security/anonymization", description: "Anonymize data", keywords: ["anonymize", "privacy", "data"] },
      { name: "Wi-Fi & devices", url: "/security/wifi", description: "Connection info and how to see devices on your network", keywords: ["wifi", "network", "devices", "router", "lan"] },
      { name: "Vulnerability Scanner", url: "/vulnerability-scanner", description: "Scan for vulnerabilities", keywords: ["vulnerability", "scan", "security"] },
      { name: "IP Lookup", url: "/ip-lookup", description: "Lookup IP information", keywords: ["ip", "lookup", "network"] },
      { name: "Email Check", url: "/email-check", description: "Check email validity", keywords: ["email", "check", "validate"] },
    ]
  },
  {
    name: "Color Tools",
    description: "Color manipulation and analysis tools",
    icon: "color",
    priority: CATEGORY_PRIORITY["Color Tools"],
    isVisible: true,
    items: [
      { name: "Color Contrast Checker", url: "/color-contrast", description: "Check color contrast", keywords: ["color", "contrast", "accessibility"] },
      { name: "Color Converter", url: "/color-converter", description: "Convert color formats", keywords: ["color", "convert", "hex", "rgb"] },
    ]
  },
  {
    name: "Math & Calculations",
    description: "Mathematical and calculation tools",
    icon: "calculator",
    priority: CATEGORY_PRIORITY["Math & Calculations"],
    isVisible: true,
    items: [
      { name: "Scientific Calculator", url: "/calculator", description: "Advanced calculator", keywords: ["calculator", "math", "scientific"] },
      { name: "Unit Converter", url: "/unit-convert", description: "Convert units", keywords: ["unit", "convert", "measurement"] },
      { name: "Percentage Calculator", url: "/percentage", description: "Calculate percentages", keywords: ["percentage", "calculate", "math"] },
    ]
  },
  {
    name: "Analytics",
    description: "Social media and platform analytics",
    icon: "analytics",
    priority: CATEGORY_PRIORITY["Analytics"],
    isVisible: true,
    items: [
      { name: "YouTube Analytics", url: "/analytics/youtube", description: "YouTube channel analytics", keywords: ["youtube", "analytics", "channel"] },
      { name: "Twitch Analytics", url: "/analytics/twitch", description: "Twitch stream analytics", keywords: ["twitch", "analytics", "stream"] },
      { name: "TikTok Analytics", url: "/analytics/tiktok", description: "TikTok account analytics", keywords: ["tiktok", "analytics", "account"] },
      { name: "X/Twitter Analytics", url: "/analytics/twitter", description: "Twitter/X analytics", keywords: ["twitter", "x", "analytics"] },
      { name: "Threads Analytics", url: "/analytics/threads", description: "Threads analytics", keywords: ["threads", "analytics", "meta"] },
      { name: "Facebook Analytics", url: "/analytics/facebook", description: "Facebook page analytics", keywords: ["facebook", "analytics", "page"] },
      { name: "Spotify Analytics", url: "/analytics/spotify", description: "Spotify music analytics", keywords: ["spotify", "analytics", "music"] },
      { name: "Steam Analytics", url: "/analytics/steam", description: "Steam game analytics", keywords: ["steam", "analytics", "gaming"] },
    ]
  },
  {
    name: "Resources",
    description: "Documentation, updates, and helpful information",
    icon: "book-open",
    priority: CATEGORY_PRIORITY["Resources"],
    isVisible: true,
    items: [
      { name: "Changelog", url: "/changelog", description: "Latest updates and improvements", keywords: ["changelog", "updates", "releases"], isPopular: true },
      { name: "Blog", url: "/blog", description: "Latest blog posts", keywords: ["blog", "posts", "articles"] },
      { name: "Portfolio", url: "/projects", description: "Project portfolio", keywords: ["portfolio", "projects", "work"] },
      { name: "Resume", url: "/resume", isExternal: true, description: "Download resume", keywords: ["resume", "cv", "download"] },
    ]
  },
];

// Export all categories combined
export const navigationCategories: NavigationCategory[] = [
  ...CORE_CATEGORIES,
  ...SECONDARY_CATEGORIES,
  ...SPECIALIZED_CATEGORIES,
];
